import React, { useState, useEffect, useMemo } from 'react';
import './BrandShowcase.css';

const BrandShowcase = () => {
  // 使用useMemo缓存品牌数组
  const allBrands = useMemo(() => [
    { id: 1, name: 'Bugatti', logo: '/image/logo/bugatti-logo.png' },
    { id: 2, name: 'Ferrari', logo: '/image/logo/ferrari-logo.png' },
    { id: 3, name: 'Lamborghini', logo: '/image/brand/lamborghini.png' },
    { id: 4, name: 'Jaguar', logo: '/image/logo/jaguar-logo.png' },
    { id: 5, name: 'Bentley', logo: '/image/logo/bentley-logo.png' },
    { id: 6, name: 'Porsche', logo: '/image/logo/porsche-logo.png' },
    { id: 7, name: 'Aston Martin', logo: '/image/logo/aston-martin-logo.png' },
    { id: 8, name: 'Rolls Royce', logo: '/image/logo/rolls-royce-logo.png' },
    { id: 9, name: 'McLaren', logo: '/image/logo/mclaren-logo.png' },
    { id: 10, name: 'Maserati', logo: '/image/logo/maserati-logo.png' }
  ], []); // 空依赖数组，因为这些数据是静态的

  // 当前中心品牌索引
  const [centerIndex, setCenterIndex] = useState(2); // 默认Lamborghini在中间
  // 动画状态简化
  const [isTransitioning, setIsTransitioning] = useState(false);

  // 使用useMemo缓存品牌描述信息
  const brandDescriptions = useMemo(() => ({
    'Bugatti': {
      text: "布加迪代表着工程的极限和奢华的顶峰，以其令人难以置信的速度和精湛的工艺闻名于世。",
      buttonText: "探索布加迪"
    },
    'Ferrari': {
      text: "法拉利代表着赛车传统和意大利激情的完美结合，每一辆车都承载着速度与优雅的精髓。",
      buttonText: "驾驶法拉利"
    },
    'Lamborghini': {
      text: "没有汽车品牌像兰博基尼那样迷人。剪刀门，V10和V12发动机，咆哮的排气声 — 它们的异域车型是炫耀的代名词。",
      buttonText: "购买兰博基尼"
    },
    'Jaguar': {
      text: "捷豹汽车融合了优雅的英国设计与猛兽般的性能，带来独特的驾驶体验和非凡的奢华感。",
      buttonText: "体验捷豹"
    },
    'Bentley': {
      text: "宾利代表着英国豪华汽车制造的顶峰，每一辆车都体现了无与伦比的工艺和舒适性。",
      buttonText: "定制您的宾利"
    },
    'Porsche': {
      text: "保时捷以精准工程和卓越性能著称，为驾驶者提供最纯粹的驾驶乐趣和日常实用性。",
      buttonText: "选择保时捷"
    },
    'Aston Martin': {
      text: "阿斯顿·马丁融合了英国风格与运动性能，创造出既优雅又充满力量的标志性汽车。",
      buttonText: "预订阿斯顿·马丁"
    },
    'Rolls Royce': {
      text: "劳斯莱斯是终极奢华的象征，以精湛的手工艺和无与伦比的舒适度创造真正的移动艺术品。",
      buttonText: "发现劳斯莱斯"
    },
    'McLaren': {
      text: "迈凯伦将F1赛车的技术带入街道，打造出在速度、性能和创新方面无与伦比的超级跑车。",
      buttonText: "驾驭迈凯伦"
    },
    'Maserati': {
      text: "玛莎拉蒂结合了意大利设计美学与令人振奋的性能，每辆车都有着独特的引擎声浪和驾驶感受。",
      buttonText: "试驾玛莎拉蒂"
    }
  }), []); // 空依赖数组，因为这些数据是静态的

  // 当前显示的品牌信息
  const [brandInfo, setBrandInfo] = useState(brandDescriptions['Lamborghini']);
  
  // 生成当前需要显示的5个品牌
  const getVisibleBrands = () => {
    const visibleBrands = [];
    const totalBrands = allBrands.length;
    
    // 显示从左到右的5个品牌
    for (let i = -2; i <= 2; i++) {
      const index = (centerIndex + i + totalBrands) % totalBrands;
      const position = i === 0 ? 'center' : 
                      i === -1 ? 'left-inner' :
                      i === 1 ? 'right-inner' :
                      i === -2 ? 'left-outer' : 'right-outer';
      
      visibleBrands.push({
        ...allBrands[index],
        position,
        index
      });
    }
    
    return visibleBrands;
  };
  
  // 获取可见品牌
  const visibleBrands = getVisibleBrands();
  
  // 处理品牌点击
  const handleBrandClick = (brandIndex) => {
    if (brandIndex === centerIndex || isTransitioning) return; // 中间品牌或过渡中不响应点击
    
    setIsTransitioning(true);
    setCenterIndex(brandIndex);
    
    // 过渡结束后重置状态
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500); // 与CSS过渡时间相同
  };

  // 更新当前品牌信息
  useEffect(() => {
    const currentBrand = allBrands[centerIndex];
    setBrandInfo(brandDescriptions[currentBrand.name]);
  }, [centerIndex, allBrands, brandDescriptions]);

  return (
    <section className="brand-showcase" id="brand-showcase">
      <div className="brand-container">
        <div className="brand-header">
          <h3 className="brand-title">POPULAR EXOTIC & LUXURY RENTAL MAKES</h3>
          <p className="brand-subtitle">
            The finest purveyors of supercars, sports cars, and limos
          </p>
        </div>
        
        <div className={`brand-carousel ${isTransitioning ? 'transitioning' : ''}`}>
          {visibleBrands.map((brand) => (
            <div 
              key={brand.id} 
              className={`brand-item ${brand.position}`}
              onClick={() => handleBrandClick(brand.index)}
              data-brand={brand.name}
            >
              <img src={brand.logo} alt={brand.name} className="brand-logo" />
            </div>
          ))}
        </div>
        
        <div className="brand-info">
          <div className="brand-feature-text">
            <p>{brandInfo.text}</p>
          </div>
          <div className="brand-action">
            <button className="brands-all-btn">{brandInfo.buttonText}</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandShowcase; 